<template>
  <v-flex d-flex>
    <v-container fluid>
      <v-fab-transition>
        <v-btn v-show="datatable.ready" fixed bottom right dark fab color="pink" @click="refresh" :loading="datatable.loading">
          <v-icon>cached</v-icon>
        </v-btn>
      </v-fab-transition>
      <v-card>
        <image-date-picker-dialog
          v-model="model.date"
          :textProps="{ 'disabled': datatable.loading }"
          :pickerProps="{ 'max': this.todayDate }">
        </image-date-picker-dialog>
        <v-container fluid class="pa-0">
          <v-card color="grey lighten-5">
            <v-card-title primary-title>
              <h3 class="headline mb-0">Currencies</h3>
            </v-card-title>
            <v-card-actions>
                <v-radio-group v-model="currencySelected" row class="ma-2">
                  <v-flex v-for="currency in currencies" :key="currency.name" >
                    <v-radio class="mr-4"
                      :value="currency.name"
                      :color="currency.color"
                      @click.native="currencySelect(currency.name)" 
                    >
                      <div slot="label">
                        <v-icon :color="currency.color">{{currency.icon}}</v-icon>
                        <strong v-bind:class="currency.textColor"> {{currency.name}}</strong>
                      </div>
                    </v-radio>
                  </v-flex>
                </v-radio-group>
            </v-card-actions>
          </v-card>
        </v-container>

        <v-tabs v-model="tabActive" class="pa-1">
          <v-tab ripple >
            Manual Actions
          </v-tab>
          <v-tab-item >
            <ManualActions
              v-if="permission['manual_actions']"
              :date="date"
              :token="currencySelected"
              :datas="this.data"
              :loading="datatable.loading"
              @refresh="refresh"
              @scrollToSaleMonitoring="scrollToSaleMonitoring"
            >
              <template slot="toolbar-content">
                <!-- CONTENT -->
              </template>
            </ManualActions>
          </v-tab-item>
          <v-tab ripple >
            Monitoring
          </v-tab>
          <v-tab-item >
              <div v-if="permission['sale_monitoring']" ref="saleMonitoring" style="position: relative">
                <LoaderBox v-if="datatable.loading" absolute />
                <div v-for="datatableData in datatableDatas" :key="datatableData.token" class="mt-3" v-if="currencySelected.includes(datatableData.token)">
                  <div v-if="!datatableData" class="elevation-1 white text-xs-center pa-3" style="transform: translateY(0px);">
                    <span>{{ NO_DATA_TEXT }}</span>
                  </div>
                  <template v-else>
                    <div style="position: relative">
                      <v-card class="grey lighten-5" >
                        <v-card-title primary-title>
                          <h3 class="headline mb-0">{{datatableData.token}}</h3>
                        </v-card-title>
                        <v-card-text class="grey lighten-5" >
                          <v-tabs v-model="tabs[datatableData.token]" show-arrows >
                            <v-tab v-for="item in datatableData['values']" :key="item.key" ripple >
                              {{ titleText(item.key) }}
                            </v-tab>
                            <v-tab-item v-for="item in datatableData['values']" :key="item.key" >
                              <v-card flat>
                                <v-card-text>
                                  <MonitorTable
                                    ref="monitorDataTable"
                                    :title="item.key"
                                    :items="item.value"
                                    :schema="getSchema(item.key)"
                                    :onInsert="form => onInsert(form, item.key)"
                                    :onEdit="form => onEdit(form, item.key)"
                                    :onDelete="form => onDelete(form, item.key)"
                                  />
                                </v-card-text>
                              </v-card>
                            </v-tab-item>
                          </v-tabs>
                        </v-card-text>
                      </v-card>
                    </div>
                  </template>
                </div>
              </div>
          </v-tab-item>
          <v-tab ripple >
            Hourly Balances
          </v-tab>
          <v-tab-item >
            <HourlyBalances v-if="this.data.length" :data="this.data[0]" :date="model.date" :currency="currencySelected" />
          </v-tab-item>
          <v-tab ripple >
            Validate Result
          </v-tab>
          <v-tab-item >
            <ValidateResult :date="date" />
          </v-tab-item>
        </v-tabs>
      </v-card>
    </v-container>
  </v-flex>
</template>

<script>
import MonitorTable from './MonitorTable'
import ManualActions from './ManualActions'
import ValidateResult from './ValidateResult'
import HourlyBalances from './HourlyBalances'
import MongoConnector from '../../services/mongo-connector.service'
import MongoService from '../../services/mongo.service'
import UserService from '../../services/user.service'
import { getDefaultConnectorSchema } from '../../configs/schemas'
import { objectToArray, isArray, isObject, isArrayType, isObjectType, titleText, compareString } from '../../libs/utils'
import { NO_DATA_TEXT, FIELD_ID } from '../../libs/mongo-datatable'
import LoaderBox from '../../components/LoaderBox'
import ImageDatePickerDialog from '../../components/ImageDatePickerDialog'

export default {
  props: {
    date: { type: String, required: false }
  },
  data () {
    const service = new MongoConnector(getDefaultConnectorSchema(undefined, 'historical-sales'))
    return {
      currencies: [
        {name: 'XRP', icon: 'cf-xrp', color: 'light-blue darken-1', textColor: 'light-blue--text text--darken-1' },
        {name: 'ETH', icon: 'cf-eth', color: 'grey darken-2', textColor: 'grey--text text--darken-3' },
        {name: 'MESG', icon: '', color: 'purple darken-2', textColor: 'purple--text text--darken-3' },
        {name: 'UND', icon: '', color: 'blue accent-3', textColor: 'blue--text text--accent-3' },
        {name: 'BHD', icon: '', color: 'red darken-2', textColor: 'red--text text--darken-2' },
        {name: 'BDP', icon: '', color: 'purple darken-2', textColor: 'purple--text text--darken-3' },
      ],
      currencySelected: 'XRP',
      permission: {
        'sale_monitoring': UserService.hasPermission('sale-monitoring', 'sale_monitoring'),
        'manual_actions': UserService.hasPermission('sale-monitoring', 'manual_actions')
      },
      service,
      NO_DATA_TEXT,
      titleText,
      tabs: {
        /**
         * XRP: 0
         */
      },
      model: {
        date: new Date().toISOString().split('T')[0]
      },
      data: [],
      datatable: {
        ready: false,
        loading: false,
        pagination: {
          page: 1,
          rowsPerPage: 8,
          totalItems: 0
        }
      },
      tabActive: 0
    }
  },
  computed: {
    todayDate () {
      /* tslint:disable-next-line */
      this.model.date
      return new Date().toISOString().split('T')[0]
    },
    datatableDatas () {
      if (this.data) {
        const results = []
        for ( const result of this.data) {
          const otherProps = {}
          if (result) {
            let targetResult = Object.keys(result)
              .reduce((obj, key) => {
                if (isArray(result[key])) {
                  obj[key] = result[key]
                } else if (isObject(result[key])) {
                  result[key][FIELD_ID] = result[key].hasOwnProperty(FIELD_ID) ? result[key][FIELD_ID] : `${result[FIELD_ID]}_${key}`
                  obj[key] = [ result[key] ]
                } else {
                  otherProps[key] = result[key]
                }
                return obj
              }, {})
            targetResult = {
              '_status': [otherProps],
              ...targetResult
            }
            const resultArray = objectToArray(targetResult).sort((a, b) => {
              if (['_status', 'total'].includes(a['key'])) return -1
              if (['_status', 'total'].includes(b['key'])) return 1
              return compareString(a['key'], b['key'])
            })
            results.push({
              'token': result.hasOwnProperty('token') ? result['token'] : 'XRP',
              'created_at': result['created_at'],
              'sale_date_id': otherProps.sale_date_id,
              'values': resultArray
            })
            // set tab name
            results.forEach(dataTableData => {
              this.tabs[dataTableData.token] = 0
            })
          }
        }
        return results
      }
      return null
    }
  },
  watch: {
    'date': {
      immediate: true,
      handler (val) {
        this.model.date = val
      }
    },
    'model.date': {
      immediate: true,
      handler (val) {
        this.$nextTick(() => {
          this.$router.push({ name: 'saleMonitoring', query: { date: val } })
          this.refresh()
        })
      }
    }
  },
  methods: {
    getSchema (key) {
      if (key === '_status') {
        return Object.keys(this.service.schema).reduce((c, k) => {
          if (!isArrayType(this.service.schema[k].type) && !isObjectType(this.service.schema[k].type)) {
            c[k] = this.service.schema[k]
          }
          return c
        }, {})
      }
      return this.service.schema[key] && this.service.schema[key].type && this.service.schema[key].type[0]
    },
    async currencySelect (currentCurrency) {
      this.datatable.loading = true
      try {
        const [ data ] = await Promise.all([
          MongoService.sales.monitoring({date: new Date(this.model.date), currency: currentCurrency })
        ])
        this.data = data
      } catch (error) {
        console.error(error)
      }
      this.datatable.loading = false
      this.datatable.ready = true
    },
    async refresh () {
      this.datatable.loading = true
      try {
        const [ data ] = await Promise.all([
          MongoService.sales.monitoring({date: new Date(this.model.date), currency: this.currencySelected })
        ])
        this.data = data
      } catch (error) {
        console.error(error)
      }
      this.datatable.loading = false
      this.datatable.ready = true
    },
    onInsert (form, key) {
      console.log('onInsert', form, key)
      this.refresh()
      return true
    },
    onEdit (form, key) {
      console.log('onEdit', form, key)
      this.refresh()
      return true
    },
    onDelete (form, key) {
      console.log('onDelete', form, key)
      this.refresh()
      return true
    },
    scrollToSaleMonitoring () {
      if (this.$refs.saleMonitoring) {
        $('html, body').animate({
          scrollTop: $(this.$refs.saleMonitoring).offset().top - 10
        }, 300)
      }
    }
  },
  components: {
    MonitorTable,
    ManualActions,
    ValidateResult,
    HourlyBalances,
    LoaderBox,
    ImageDatePickerDialog
  }
}
</script>
